import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { portalApiConfig, createApiInstance } from '../utils/apiConfig';

// Create Context
const PatientPortalContext = createContext(null);

// Create Provider Component
export const PatientPortalProvider = ({ children }) => {
  const [patient, setPatient] = useState(null); // Store patient data
  const [token, setToken] = useState(localStorage.getItem('portalAuthToken')); // Get token from local storage initially
  const [loading, setLoading] = useState(false); // Loading state for auth operations
  const [portalAccess, setPortalAccess] = useState(null); // Store portal access info

  // Effect to check token validity and fetch patient profile on initial load or token change
  useEffect(() => {
    const verifyTokenAndFetchPatient = async () => {
      if (token) {
        setLoading(true);

        try {
          // Create isolated axios instance for portal requests
          const portalAxios = createPortalAxiosInstance(token);

          // Fetch patient health overview using the portal token
          const response = await portalAxios.get(portalApiConfig.getPath(portalApiConfig.endpoints.healthOverview));
          console.log('Portal health overview response:', response.data);
          setPatient(response.data.data.patient); // Store patient data
          setPortalAccess(response.data.data.portalAccess); // Store portal access info
        } catch (error) {
          // Handle errors (e.g., token expired, invalid)
          console.error('Portal token verification failed:', error.response?.data || error.message);

          // Only clear portal token if this is actually a portal authentication error
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.log('Clearing invalid portal token');
            localStorage.removeItem('portalAuthToken');
            setToken(null);
            setPatient(null);
            setPortalAccess(null);
          }
        } finally {
          setLoading(false);
        }
      } else {
        // No token found - this is normal for login page
        setPatient(null);
        setPortalAccess(null);
        setLoading(false);
      }
    };

    verifyTokenAndFetchPatient();
  }, []); // Empty dependency array ensures this runs only once on mount

  // Helper function to create isolated axios instance for portal requests
  const createPortalAxiosInstance = (authToken) => {
    const instance = createApiInstance({
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    // Add response interceptor specifically for portal requests
    instance.interceptors.response.use(
      response => response,
      error => {
        // Handle portal-specific 401 errors
        if (error.response?.status === 401) {
          console.warn('Portal authentication failed - token may be expired');
          // Don't automatically clear token here, let the calling code handle it
        }
        return Promise.reject(error);
      }
    );

    return instance;
  };



  // Login function with email/phone credentials
  const loginWithCredentials = async (username, password) => {
    try {
      setLoading(true);

      // Create isolated axios instance for login (no auth header needed)
      const loginAxios = createApiInstance();

      console.log('Attempting portal login with credentials');
      const response = await loginAxios.post(portalApiConfig.getPath(portalApiConfig.endpoints.login), { username, password });
      const { token: newToken, data: { patient: patientData, portalAccess: accessData } } = response.data;

      // Store token and update state
      localStorage.setItem('portalAuthToken', newToken);
      setToken(newToken);
      setPatient(patientData);
      setPortalAccess(accessData);
      setLoading(false);

      console.log('Portal login successful');
      return { success: true };
    } catch (error) {
      console.error('Portal login with credentials failed:', error.response?.data || error.message);

      // Clear any existing portal data on login failure
      localStorage.removeItem('portalAuthToken');
      setToken(null);
      setPatient(null);
      setPortalAccess(null);
      setLoading(false);

      let errorMessage = 'Login failed';
      if (error.response?.status === 401) {
        errorMessage = error.response.data.message || 'Invalid credentials';
      } else if (error.response?.status === 423) {
        errorMessage = 'Account is temporarily locked. Please try again later.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data.message || 'Please enter a valid email address or phone number';
      }

      return { success: false, message: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    setLoading(true);
    localStorage.removeItem('portalAuthToken'); // Remove token from local storage
    setToken(null); // Clear state
    setPatient(null);
    setPortalAccess(null);
    setLoading(false);
  };

  // Function to get authenticated axios instance for portal API calls
  const getPortalAxios = () => {
    if (!token) {
      throw new Error('No portal authentication token available');
    }

    return createPortalAxiosInstance(token);
  };

  // Function to fetch health overview data
  const fetchHealthOverview = async () => {
    try {
      const portalAxios = getPortalAxios();
      const response = await portalAxios.get(portalApiConfig.getPath(portalApiConfig.endpoints.healthOverview));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching health overview:', error);
      // Handle token expiration specifically
      if (error.response?.status === 401) {
        console.log('Portal token expired during health overview fetch');
        localStorage.removeItem('portalAuthToken');
        setToken(null);
        setPatient(null);
        setPortalAccess(null);
      }
      throw error;
    }
  };

  // Function to fetch timeline data
  const fetchTimeline = async () => {
    try {
      const portalAxios = getPortalAxios();
      const response = await portalAxios.get(portalApiConfig.getPath(portalApiConfig.endpoints.timeline));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching timeline:', error);
      // Handle token expiration specifically
      if (error.response?.status === 401) {
        console.log('Portal token expired during timeline fetch');
        localStorage.removeItem('portalAuthToken');
        setToken(null);
        setPatient(null);
        setPortalAccess(null);
      }
      throw error;
    }
  };

  // Function to fetch problems data
  const fetchProblems = async () => {
    try {
      const portalAxios = getPortalAxios();
      const response = await portalAxios.get(portalApiConfig.getPath(portalApiConfig.endpoints.problems));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching problems:', error);
      // Handle token expiration specifically
      if (error.response?.status === 401) {
        console.log('Portal token expired during problems fetch');
        localStorage.removeItem('portalAuthToken');
        setToken(null);
        setPatient(null);
        setPortalAccess(null);
      }
      throw error;
    }
  };

  // Function to fetch medications data
  const fetchMedications = async () => {
    try {
      const portalAxios = getPortalAxios();
      const response = await portalAxios.get(portalApiConfig.getPath(portalApiConfig.endpoints.medications));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching medications:', error);
      // Handle token expiration specifically
      if (error.response?.status === 401) {
        console.log('Portal token expired during medications fetch');
        localStorage.removeItem('portalAuthToken');
        setToken(null);
        setPatient(null);
        setPortalAccess(null);
      }
      throw error;
    }
  };

  // Check if portal access is expired
  const isAccessExpired = () => {
    if (!portalAccess?.expiresAt) return false;
    return new Date(portalAccess.expiresAt) < new Date();
  };

  // Get days until expiration
  const getDaysUntilExpiration = () => {
    if (!portalAccess?.expiresAt) return null;
    const expirationDate = new Date(portalAccess.expiresAt);
    const today = new Date();
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Value provided to consuming components
  const value = {
    patient,
    token,
    portalAccess,
    isAuthenticated: !!patient, // Simple check if patient object exists
    loading,
    loginWithCredentials,
    logout,
    fetchHealthOverview,
    fetchTimeline,
    fetchProblems,
    fetchMedications,
    getPortalAxios,
    isAccessExpired,
    getDaysUntilExpiration
  };

  return (
    <PatientPortalContext.Provider value={value}>
      {children}
    </PatientPortalContext.Provider>
  );
};

// Custom hook to use the PatientPortalContext
export const usePatientPortal = () => {
  const context = useContext(PatientPortalContext);
  if (!context) {
    throw new Error('usePatientPortal must be used within a PatientPortalProvider');
  }
  return context;
};
